import type {
  AddNafMembershipApiData,
  AddNafMembershipApiResponses,
  GetMembershipsForUserGroupApiData,
  GetMembershipsForUserGroupApiResponses,
  IsNafMemberApiData,
  IsNafMemberApiResponses,
  MembershipActivateApiData,
  MembershipActivateApiResponses,
  MembershipCheckCodeForSlugApiData,
  MembershipCheckCodeForSlugApiResponses,
  MembershipDiscountCreateApiData,
  MembershipDiscountCreateApiResponses,
  MembershipDiscountDeleteApiData,
  MembershipDiscountDeleteApiResponses,
  MembershipDiscountGetApiData,
  MembershipDiscountGetApiResponses,
  MembershipDiscountListApiData,
  MembershipDiscountListApiResponses,
  MembershipDiscountUpdateApiData,
  MembershipDiscountUpdateApiResponses,
  MembershipFromSlugRecord,
  MembershipListFromSlugApiData,
  MembershipProgramCreateApiData,
  MembershipProgramCreateApiResponses,
  MembershipProgramDeleteApiData,
  MembershipProgramDeleteApiResponses,
  MembershipProgramGetApiData,
  MembershipProgramGetApiResponses,
  MembershipProgramListApiData,
  MembershipProgramListApiResponses,
  MembershipProgramUpdateApiData,
  MembershipProgramUpdateApiResponses,
  PartnerMembershipDiscountActivitiesApiData,
  PartnerMembershipDiscountActivitiesApiResponses,
  PartnerMembershipDiscountCreateApiData,
  PartnerMembershipDiscountCreateApiResponses,
  PartnerMembershipDiscountDeleteApiData,
  PartnerMembershipDiscountDeleteApiResponses,
  PartnerMembershipDiscountGetApiData,
  PartnerMembershipDiscountGetApiResponses,
  PartnerMembershipDiscountListApiData,
  PartnerMembershipDiscountListApiResponses,
  PartnerMembershipDiscountListForBrandApiData,
  PartnerMembershipDiscountListForBrandApiResponses,
  PartnerMembershipDiscountPatchApiData,
  PartnerMembershipDiscountPatchApiResponses,
  PartnerMembershipProgramActivitiesApiData,
  PartnerMembershipProgramActivitiesApiResponses,
  PartnerMembershipProgramAssignApiData,
  PartnerMembershipProgramAssignApiResponses,
  PartnerMembershipProgramAttachableDiscountsApiData,
  PartnerMembershipProgramAttachableDiscountsApiResponses,
  PartnerMembershipProgramCreateApiData,
  PartnerMembershipProgramCreateApiResponses,
  PartnerMembershipProgramDeleteApiData,
  PartnerMembershipProgramDeleteApiResponses,
  PartnerMembershipProgramGetApiData,
  PartnerMembershipProgramGetApiResponses,
  PartnerMembershipProgramListApiData,
  PartnerMembershipProgramListApiResponses,
  PartnerMembershipProgramMembersApiData,
  PartnerMembershipProgramMembersApiResponses,
  PartnerMembershipProgramPatchApiData,
  PartnerMembershipProgramPatchApiResponses,
  PartnerMembershipProgramRemoveMemberApiData,
  PartnerMembershipProgramRemoveMemberApiResponses
} from '@navio/nsdk';

import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';
import { partnerProgramCreateFormData, partnerProgramPatchFormData } from './membershipProgramFormData';

/** Mapped campaign redirect until @navio/nsdk includes campaignCode. */
type MembershipFromSlugApiRecord = MembershipFromSlugRecord & {
  campaignCode?: string | null;
};

type MembershipListFromSlugApiResponsesWithCampaign = {
  200: MembershipFromSlugApiRecord;
};

export const getMembershipUrls = {
  getPaginatedMembershipDiscounts: dataEndpoint<MembershipDiscountListApiData, MembershipDiscountListApiResponses>({
    getUrl: (queryParams) => apiUrl('memberships-discounts/', queryParams)
  }),
  getMembershipDiscount: dataEndpoint<MembershipDiscountGetApiData, MembershipDiscountGetApiResponses>({
    getUrl: ({ pk }) => apiUrl(`memberships-discounts/${pk}/`)
  }),
  postMembershipDiscount: responseEndpoint<MembershipDiscountCreateApiData, MembershipDiscountCreateApiResponses>({
    getUrl: () => apiUrl('memberships-discounts/'),
    getBody: (data) => data
  }),
  patchMembershipDiscount: responseEndpoint<MembershipDiscountUpdateApiData, MembershipDiscountUpdateApiResponses>({
    getUrl: ({ pk }) => apiUrl(`memberships-discounts/${pk}/`),
    getBody: ({ pk: _, ...body }) => body
  }),
  deleteMembershipDiscount: dataEndpoint<MembershipDiscountDeleteApiData, MembershipDiscountDeleteApiResponses>({
    getUrl: ({ pk }) => apiUrl(`memberships-discounts/${pk}/`)
  }),
  getMembershipProgram: dataEndpoint<MembershipProgramGetApiData, MembershipProgramGetApiResponses>({
    getUrl: ({ pk }) => apiUrl(`membership-programs/${pk}/`)
  }),
  getPaginatedMembershipPrograms: dataEndpoint<MembershipProgramListApiData, MembershipProgramListApiResponses>({
    getUrl: (queryParams) => apiUrl('membership-programs/', queryParams)
  }),
  postMembershipProgram: responseEndpoint<MembershipProgramCreateApiData, MembershipProgramCreateApiResponses>({
    getUrl: () => apiUrl('membership-programs/'),
    getBody: (data) => data
  }),
  patchMembershipProgram: responseEndpoint<MembershipProgramUpdateApiData, MembershipProgramUpdateApiResponses>({
    getUrl: ({ pk }) => apiUrl(`membership-programs/${pk}/`),
    getBody: ({ pk: _, ...body }) => body
  }),
  deleteMembershipProgram: dataEndpoint<MembershipProgramDeleteApiData, MembershipProgramDeleteApiResponses>({
    getUrl: ({ pk }) => apiUrl(`membership-programs/${pk}/`)
  }),
  getMembershipProgramsForUserGroup: dataEndpoint<
    GetMembershipsForUserGroupApiData,
    GetMembershipsForUserGroupApiResponses
  >({
    getUrl: (queryParams) => apiUrl('memberships/get-memberships-for-user-group/', queryParams)
  }),
  getMembershipFromSlug: dataEndpoint<MembershipListFromSlugApiData, MembershipListFromSlugApiResponsesWithCampaign>({
    getUrl: ({ slug, ...queryParams }) => apiUrl(`memberships/${slug}/`, queryParams)
  }),
  postActivateMembership: responseEndpoint<MembershipActivateApiData, MembershipActivateApiResponses>({
    getUrl: ({ slug }) => apiUrl(`memberships/${slug}/activate/`),
    getBody: ({ authCode, userGroupId, brandDomain }) => ({ authCode, userGroupId, brandDomain })
  }),
  getVerifyMembershipCode: dataEndpoint<MembershipCheckCodeForSlugApiData, MembershipCheckCodeForSlugApiResponses>({
    getUrl: ({ slug, ...queryParams }) => apiUrl(`memberships/${slug}/check-code/`, queryParams)
  }),
  postNafMembership: responseEndpoint<AddNafMembershipApiData, AddNafMembershipApiResponses>({
    getUrl: () => apiUrl('memberships/add-naf-membership/'),
    getBody: (data) => data
  }),
  getIsNAFMember: dataEndpoint<IsNafMemberApiData, IsNafMemberApiResponses>({
    getUrl: (queryParams) => apiUrl('memberships/is-naf-member/', queryParams)
  }),
  getPaginatedPartnerMembershipPrograms: dataEndpoint<
    PartnerMembershipProgramListApiData,
    PartnerMembershipProgramListApiResponses
  >({
    getUrl: (queryParams) => apiUrl('membership-programs/for-partner/', queryParams)
  }),
  postPartnerMembershipProgram: responseEndpoint<
    PartnerMembershipProgramCreateApiData,
    PartnerMembershipProgramCreateApiResponses
  >({
    getUrl: ({ brandId }) => apiUrl(`brands/${brandId}/membership-programs/`),
    getHeaders: () => ({
      'Content-Type': 'multipart/form-data'
    }),
    getBody: (data) => partnerProgramCreateFormData(data),
    skipBodyTransformation: true
  }),
  getPartnerMembershipProgram: dataEndpoint<
    PartnerMembershipProgramGetApiData,
    PartnerMembershipProgramGetApiResponses
  >({
    getUrl: ({ membershipProgramId }) => apiUrl(`membership-programs/${membershipProgramId}/partner/`)
  }),
  patchPartnerMembershipProgram: responseEndpoint<
    PartnerMembershipProgramPatchApiData,
    PartnerMembershipProgramPatchApiResponses
  >({
    getUrl: ({ membershipProgramId }) => apiUrl(`membership-programs/${membershipProgramId}/partner/`),
    getHeaders: () => ({
      'Content-Type': 'multipart/form-data'
    }),
    getBody: (data) => partnerProgramPatchFormData(data),
    skipBodyTransformation: true
  }),
  deletePartnerMembershipProgram: dataEndpoint<
    PartnerMembershipProgramDeleteApiData,
    PartnerMembershipProgramDeleteApiResponses
  >({
    getUrl: ({ membershipProgramId }) => apiUrl(`membership-programs/${membershipProgramId}/partner/`)
  }),
  getPaginatedPartnerMembershipProgramMembers: dataEndpoint<
    PartnerMembershipProgramMembersApiData,
    PartnerMembershipProgramMembersApiResponses
  >({
    getUrl: ({ membershipProgramId, ...queryParams }) =>
      apiUrl(`membership-programs/${membershipProgramId}/members/`, queryParams)
  }),
  postPartnerMembershipProgramAssign: responseEndpoint<
    PartnerMembershipProgramAssignApiData,
    PartnerMembershipProgramAssignApiResponses
  >({
    getUrl: ({ membershipProgramId }) => apiUrl(`membership-programs/${membershipProgramId}/assign/`),
    getBody: ({ membershipProgramId: _, ...body }) => body
  }),
  deletePartnerMembershipProgramMember: dataEndpoint<
    PartnerMembershipProgramRemoveMemberApiData,
    PartnerMembershipProgramRemoveMemberApiResponses
  >({
    getUrl: ({ membershipProgramId, userGroupId }) =>
      apiUrl(`membership-programs/${membershipProgramId}/members/${userGroupId}/`)
  }),
  getPaginatedPartnerMembershipProgramActivities: dataEndpoint<
    PartnerMembershipProgramActivitiesApiData,
    PartnerMembershipProgramActivitiesApiResponses
  >({
    getUrl: ({ membershipProgramId, ...queryParams }) =>
      apiUrl(`membership-programs/${membershipProgramId}/activities/`, queryParams)
  }),
  getPaginatedPartnerMembershipProgramAttachableDiscounts: dataEndpoint<
    PartnerMembershipProgramAttachableDiscountsApiData,
    PartnerMembershipProgramAttachableDiscountsApiResponses
  >({
    getUrl: ({ membershipProgramId, ...queryParams }) =>
      apiUrl(`membership-programs/${membershipProgramId}/attachable-discounts/`, queryParams)
  }),
  getPaginatedPartnerMembershipDiscounts: dataEndpoint<
    PartnerMembershipDiscountListApiData,
    PartnerMembershipDiscountListApiResponses
  >({
    getUrl: (queryParams) => apiUrl('membership-discounts/for-partner/', queryParams)
  }),
  getPaginatedPartnerMembershipDiscountsForBrand: dataEndpoint<
    PartnerMembershipDiscountListForBrandApiData,
    PartnerMembershipDiscountListForBrandApiResponses
  >({
    getUrl: ({ brandId, ...queryParams }) => apiUrl(`brands/${brandId}/membership-discounts/`, queryParams)
  }),
  postPartnerMembershipDiscount: responseEndpoint<
    PartnerMembershipDiscountCreateApiData,
    PartnerMembershipDiscountCreateApiResponses
  >({
    getUrl: ({ brandId }) => apiUrl(`brands/${brandId}/membership-discounts/`),
    getBody: ({ brandId: _brandId, ...body }) => body
  }),
  getPartnerMembershipDiscount: dataEndpoint<
    PartnerMembershipDiscountGetApiData,
    PartnerMembershipDiscountGetApiResponses
  >({
    getUrl: ({ membershipDiscountId }) => apiUrl(`memberships-discounts/${membershipDiscountId}/partner/`)
  }),
  patchPartnerMembershipDiscount: responseEndpoint<
    PartnerMembershipDiscountPatchApiData,
    PartnerMembershipDiscountPatchApiResponses
  >({
    getUrl: ({ membershipDiscountId }) => apiUrl(`memberships-discounts/${membershipDiscountId}/partner/`),
    getBody: ({ membershipDiscountId: _, ...body }) => body
  }),
  deletePartnerMembershipDiscount: dataEndpoint<
    PartnerMembershipDiscountDeleteApiData,
    PartnerMembershipDiscountDeleteApiResponses
  >({
    getUrl: ({ membershipDiscountId }) => apiUrl(`memberships-discounts/${membershipDiscountId}/partner/`)
  }),
  getPaginatedPartnerMembershipDiscountActivities: dataEndpoint<
    PartnerMembershipDiscountActivitiesApiData,
    PartnerMembershipDiscountActivitiesApiResponses
  >({
    getUrl: ({ membershipDiscountId, ...queryParams }) =>
      apiUrl(`memberships-discounts/${membershipDiscountId}/activities/`, queryParams)
  })
};
